import React, { useState, useRef } from 'react';
import { Box, Button, Typography, Paper, TextField, CircularProgress, Alert } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CropIcon from '@mui/icons-material/Crop';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PanToolIcon from '@mui/icons-material/PanTool'; // Keep if used or remove. Removing usage.
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const OcrExtractor = () => {
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState(null); // { pages: [...] }
    const [activePageIndex, setActivePageIndex] = useState(0);
    const [crop, setCrop] = useState({ unit: '%', width: 50, aspect: undefined });
    const [completedCrop, setCompletedCrop] = useState(null);
    const [extractedText, setExtractedText] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [zoom, setZoom] = useState(1);
    const imgRef = useRef(null);

    const handleFileUpload = async (e) => {
        const uploadedFile = e.target.files[0];
        if (!uploadedFile) return;

        setFile(uploadedFile);
        setError('');
        setLoading(true);
        setExtractedText('');
        setCompletedCrop(null);

        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);

            const response = await fetch('/api/ocr/preview', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to load preview');
            }

            const data = await response.json();
            if (data.pages && data.pages.length > 0) {
                setPreviewData(data);
                setActivePageIndex(0);
            } else {
                throw new Error('No pages found in preview');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleExtract = async () => {
        if (!previewData || !imgRef.current) {
            setError('No image loaded');
            return;
        }

        setLoading(true);
        setError('');

        try {
            let imageToExtract;

            if (completedCrop && completedCrop.width && completedCrop.height) {
                // Create cropped image
                const canvas = document.createElement('canvas');
                const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
                const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

                canvas.width = completedCrop.width;
                canvas.height = completedCrop.height;
                const ctx = canvas.getContext('2d');

                ctx.drawImage(
                    imgRef.current,
                    completedCrop.x * scaleX,
                    completedCrop.y * scaleY,
                    completedCrop.width * scaleX,
                    completedCrop.height * scaleY,
                    0,
                    0,
                    completedCrop.width,
                    completedCrop.height
                );

                const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.95));
                imageToExtract = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
            } else {
                // Use full image
                imageToExtract = file;
            }

            const formData = new FormData();
            formData.append('file', imageToExtract);

            const response = await fetch('/api/ocr/extract', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Extraction failed');
            }

            const data = await response.json();
            setExtractedText(data.text);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 1400, mx: 'auto', p: 2 }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>OCR Extractor</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Upload an image or PDF, select a region (optional), and extract text using OCR.
            </Typography>

            {/* Upload Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Button
                    component="label"
                    variant="contained"
                    startIcon={<UploadFileIcon />}
                    disabled={loading}
                >
                    Upload Image or PDF
                    <input
                        type="file"
                        hidden
                        accept="image/*,.pdf"
                        onChange={handleFileUpload}
                    />
                </Button>
                {file && (
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                        Loaded: {file.name}
                    </Typography>
                )}
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {loading && !previewData && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Preview & Crop Section */}
            {previewData && previewData.pages && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    {/* Controls Header */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
                        <Typography variant="h6">
                            Preview
                            {previewData.pages.length > 1 && (
                                <Box component="span" sx={{ fontSize: '0.8em', color: 'text.secondary', ml: 1 }}>
                                    (Page {activePageIndex + 1} of {previewData.pages.length})
                                </Box>
                            )}
                        </Typography>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                            {/* Pagination Controls */}
                            {previewData.pages.length > 1 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setActivePageIndex(p => Math.max(0, p - 1))}
                                        disabled={activePageIndex === 0}
                                    >
                                        Prev
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setActivePageIndex(p => Math.min(previewData.pages.length - 1, p + 1))}
                                        disabled={activePageIndex === previewData.pages.length - 1}
                                    >
                                        Next
                                    </Button>
                                </Box>
                            )}

                            {/* Zoom Controls */}
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}>
                                <Typography variant="caption" sx={{ mr: 1 }}>Zoom:</Typography>
                                <Button size="small" variant="outlined" onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} disabled={zoom <= 0.5}>-</Button>
                                <Button size="small" variant="outlined" onClick={() => setZoom(1)} disabled={zoom === 1}>{Math.round(zoom * 100)}%</Button>
                                <Button size="small" variant="outlined" onClick={() => setZoom(z => Math.min(3, z + 0.25))} disabled={zoom >= 3}>+</Button>
                            </Box>
                        </Box>
                    </Box>

                    {/* Image Area - Unified Zoom & Crop */}
                    <Box sx={{
                        mb: 2,
                        bgcolor: '#e0e0e0', // Darker background to see paper boundaries
                        borderRadius: 1,
                        overflow: 'auto', // Enable scrolling
                        display: 'flex',
                        justifyContent: 'center',
                        maxHeight: '600px',
                        border: '1px solid #ccc',
                        p: 2
                    }}>
                        <ReactCrop
                            crop={crop}
                            onChange={(c) => setCrop(c)}
                            onComplete={(c) => setCompletedCrop(c)}
                            style={{
                                maxWidth: 'none', // Allow it to grow beyond container
                                transformOrigin: 'top left'
                            }}
                        >
                            <img
                                ref={imgRef}
                                src={previewData.pages[activePageIndex].image_data}
                                alt={`Page ${activePageIndex + 1}`}
                                style={{
                                    width: `${previewData.pages[activePageIndex].width * zoom}px` || 'auto', // Explicit pixel width for Zoom
                                    maxWidth: 'none', // Override default responsiveness to allow zoom
                                    display: 'block',
                                    backgroundColor: 'white',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                }}
                                onLoad={(e) => {
                                    // Reset crop on page change if needed, or keep it. 
                                    // Keeping it clean for now.
                                }}
                            />
                        </ReactCrop>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <TextFieldsIcon />}
                            onClick={handleExtract}
                            disabled={loading}
                        >
                            {loading ? 'Extracting...' : completedCrop && completedCrop.width > 0 ? 'Extract Selection' : 'Extract Full Page'}
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* Extracted Text Section */}
            {extractedText && (
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Extracted Text</Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => navigator.clipboard.writeText(extractedText)}
                        >
                            Copy
                        </Button>
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        rows={8}
                        variant="outlined"
                        value={extractedText}
                        onChange={(e) => setExtractedText(e.target.value)}
                        sx={{ fontFamily: 'monospace' }}
                    />
                </Paper>
            )}
        </Box>
    );
};

export default OcrExtractor;
